import { Nullable } from "../../../common/types/nullable";
import { LanguageSkillCode } from "../../talents/talentAPI";
import { CoreUserType } from "../../user/types/entities";
import { CustomerOrganizationWithCostCenters } from "../customerAPI";
import { InvoiceReferences } from "./commands";

export enum CustomerUserInviteStatus {
  UNINVITED = "UNINVITED",
  INVITE_PENDING = "INVITE_PENDING",
  INVITE_ACCEPTED = "INVITE_ACCEPTED",
}

export interface Identifier {
  id: string;
}

export enum RiskLevel {
  GREEN = "GREEN",
  YELLOW = "YELLOW",
  RED = "RED",
  UNKNOWN = "UNKNOWN",
}

export interface SalesPermission {
  verificationDate: string;
  riskLevel?: RiskLevel;
}

export interface CustomerOrganizations {
  companyId?: string;
}

export interface CustomerOrganizationsPageFilters {
  pageSize?: number;
  cursor?: string | null;
  search?: string;
}

export interface CustomerOrganizationsResult {
  id: string;
  name: string;
  businessId: string;
  customerNumber?: string;
  customerCompanyId: string;
  customerCompanyBusinessId: string;
  isRepresentative: boolean;
  salesPermission: SalesPermission;
}

export interface CustomerOrganizationsAutocompleteResult {
  id: string;
  version: number;
  name: string;
  businessId: string;
  customerCompanyId: string;
  customerCompanyBusinessId: string;
  isRepresentative: boolean;
  salesPermission: SalesPermission;
  customerNumber: string;
  type: string;
  billingAddress: Address;
  industry?: string;
  organizationInvoiceSettings: InvoiceSettings;
  organizationCustomerContacts?: Identifier[];
  salesPersonId?: string;
  salesPersonName?: string;
}

export interface CustomerCompanyIdFilter {
  customerCompanyId: string;
}

export interface CustomerCostCentersResult {
  customerCCId: string;
  customerOrgId: string;
  name: string;
  active: boolean;
}

/**
 * @deprecated Use CustomerOrganizationsResult instead
 */
export interface CustomerCompanyWithOrganizations {
  customerCompanyId: string;
  name: string;
  businessId: string;
  version: number;
  type: string;
  organizations: CustomerOrganization[];
}

export interface CustomerOrganization {
  customerCompanyId: string;
  customerOrgId: string;
  name: string;
  version: number;
  billingAddress: CustomerOrganizationAddress;
  type: string;
  businessId?: string;
  salesPermission?: SalesPermission;
}

export interface CustomerOrganizationAddress {
  street: string;
  zip: string;
  city: string;
  country: string;
}

export type Contract = {
  name: string;
  id: string;
};

export type CustomerCC = {
  id: string;
  customerCCId: string; //deprecated?
  customerOrgId: string;
  name: string;
  active: boolean;
};

export type Address = {
  street: string;
  zip: string;
  city: string;
  country?: string;
};

export type Info = {
  fi: string;
  en: string;
};

// small differences to type WorkLocation
export interface WorkLocationById {
  workLocationId: string;
  customerOrgId: string;
  contractId: string;
  name: string;
  customerOrgName: string;
  address: Address;
  customerCCIds: string[];
  info: Record<string, Info>;
  descriptionForEezy?: string;
  descriptionForTalent: string;
  descriptionForTalentEng: string;
  version: number;
  salesPersonId?: string;
  salesPersonName?: string;
  talentCustomerContactPersons?: Identifier[];
  custUsersWhoCanOrder?: string[];
  active: boolean;
}

export type WorkLocation = {
  id: string;
  customerOrgId: string;
  customerOrgName?: string;
  contractId: string;
  name: string;
  address: Address;
  customerCCs: CustomerCostCentersResult[];
  info: Record<string, Info>;
  descriptionForEezy?: string;
  descriptionForTalent: string;
  descriptionForTalentEng: string;
  version: number;
  salesPersonId?: string;
  salesPersonName?: string;
  talentCustomerContactPersons: Identifier[]; // TODO: will be changed to string[]
  active: boolean;
  additionalInfo?: string;
  custUsersWhoCanOrder?: string[];
  invoiceContactPerson: Nullable<Identifier>;
  invoiceReferences: Nullable<InvoiceReferences>;
};

export interface CustomerWorkLocationsAndContracts {
  contracts: Contract[];
  workLocations: WorkLocation[];
}

export interface CustomerWorkLocationsAndOrganizations {
  customerOrganizations: CustomerOrganizationWithCostCenters[];
  workLocations: WorkLocation[];
}

export enum DeliveryMethod {
  BY_OPERATOR = "BY_OPERATOR",
  BY_EMAIL = "BY_EMAIL",
  BY_E_INVOICE = "BY_E_INVOICE",
}

export type ByEInvoice = {
  deliveryMethod: DeliveryMethod.BY_E_INVOICE;
  deliveryAddress: {
    address: string;
    operatorId: string;
  };
};

export type ByEmail = {
  deliveryMethod: DeliveryMethod.BY_EMAIL;
  deliveryAddress: {
    address: string;
  };
};

export type ByOperator = {
  deliveryMethod: DeliveryMethod.BY_OPERATOR;
};

export type InvoiceSettings = ByEInvoice | ByEmail | ByOperator;

interface SalesAccount {
  code: string;
  description: string;
  defaultVatPercent: string;
  defaultVatCode: string;
  canUseInContract: boolean;
  canUseInGroupInternalSales: boolean;
  canUseInExternalSales: boolean;
}

interface CustomerOrgCostCenter {
  id: string;
  name: string;
  code: string;
  version: number;
  active: boolean;
}

export interface CustomerOrganizationDetails {
  id: string;
  customerCompanyId: string;
  customerCompanyName?: string;
  customerNumber: string;
  name: string;
  version: number;
  customerContacts: Identifier[];
  industry: string;
  salesPersonId: string;
  salesPersonName: string;
  billingAddress: Address;
  businessId: string;
  salesPermission: SalesPermission;
  invoiceSettings: InvoiceSettings;
  costCenters: CustomerOrgCostCenter[];
  invoiceReferences?: InvoiceReferences;
  invoiceContactPerson?: Identifier;
  isNationwide: boolean;
  isRepresentative: boolean;
}

export interface CustomerCostCenterDetails {
  customerCCId: string;
  customerOrgId: string;
  name: string;
  version: number;
  code: string;
  salesAccount?: SalesAccount;
  invoiceReferences: InvoiceReferences;
  invoiceContactPerson: Identifier;
  active: boolean;
}

export interface CustomerCostCenterDetailsFilter {
  customerCostCenterId: string;
}

export interface CustomerCostCentersByOrgIdsFilters {
  customerOrgIds: string;
  customerCCIds: string;
}

export interface OrderTemplateFilters {
  contractId: string;
}

export interface CustomerPersonsFilters {
  customerCompanyId: string;
}

export interface CustomerUserAccount {
  firstName: string;
  lastName: string;
  phone: string;
  title: string;
  emailAddress: string;
  customerUserCompanyId: string;
  customerUserWorkLocationIds?: string[];
  customerUserInviteStatus: CustomerUserInviteStatus;
  version: number;
  enabled: boolean;
}

export interface CustomerUser {
  id: string;
  type: string;
  account: CustomerUserAccount;
}

export interface CityAndPostOfficeByZip {
  zip: string;
}

export interface CityAndPostOfficeByZipResponse {
  city: string;
  postOffice: string;
}

export interface WorkTitlesAutoCompleteFilters {
  term: string;
  language: LanguageSkillCode;
}

export interface WorkTitlesAutoCompleteResponse {
  id: string;
  name: string;
  fieldOfWorkId: string;
}

export interface CustomerUsersAutocompleteFilters {
  search: string;
  withCompanyName?: "true";
}

export interface CustomerUsersAutocompleteThunkFilters {
  search: string;
  onlyActive?: boolean;
  withCompanyName?: boolean;
}

export interface CustomerUsersAutocompleteResponse {
  id: string;
  version: number;
  firstName: string;
  lastName: string;
  type: CoreUserType.CUSTOMER;
  title: Nullable<string>; // null until customer users are defined with a title
  customerCompanyId: string;
  customerCompanyName?: string;
  workLocationIds: string[];
  registered: boolean;
  enabled: Nullable<boolean>;
}
